import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { AreaListItem, ListResult } from '@tww/shared';
import { AREA_STATUSES, AREA_STATUS_LABELS, fmtHa } from '@tww/shared';
import { api } from '../../api/client';
import { useLookups } from '../../api/hooks';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable, Pager, type Column } from '../../ui/DataTable';
import { ErrorState, Skeleton } from '../../ui/basics';
import { Icon } from '../../ui/Icon';
import { useListState } from '../../ui/useListState';
import { AreaStatusPill, MonitoringPill, plantingLabel } from './common';
import { Dropdown } from '../../ui/Dropdown';

export function AreasListPage() {
  const nav = useNavigate();
  const { can } = useAuth();
  const lookups = useLookups();
  const { state, update, setFilter, toggleSort, apiParams } = useListState({ sort: 'name', order: 'asc' });
  const q = useQuery({ queryKey: ['areas', apiParams], queryFn: () => api.get<ListResult<AreaListItem>>(`/areas?${apiParams}`), placeholderData: (p) => p });
  const facets = q.data?.facets ?? {};
  const statusCount = (s: string) => facets.status?.find((f) => f.value === s)?.count ?? 0;
  const statusSel = state.filters.status?.[0] ?? '';
  const states = lookups.data?.find((l) => l.key === 'states')?.values ?? [];
  const stateSel = state.filters.state?.[0] ?? '';
  const columns: Column<AreaListItem>[] = [
    {
      key: 'name',
      label: 'Fläche',
      sortable: true,
      render: (a) => (
        <>
          <Link className="link" to={`/flaechen/${a.id}`}>
            {a.name}
          </Link>
          {(a.city || a.municipality) && <span className="sm">{a.city || a.municipality}</span>}
        </>
      ),
    },
    { key: 'status', label: 'Status', sortable: true, render: (a) => <AreaStatusPill status={a.status} /> },
    { key: 'region', label: 'Region', sortable: true, render: (a) => <>{a.state_label ?? '–'}{a.region && <span className="sm">{a.region}</span>}</> },
    { key: 'forester', label: 'Förster', render: (a) => (a.forester ? <Link className="link" to={`/kontakte/${a.forester.id}`}>{a.forester.display_name}</Link> : '–') },
    { key: 'owner', label: 'Waldbesitzer', render: (a) => (a.owner ? <Link className="link" to={`/kontakte/${a.owner.id}`}>{a.owner.display_name}</Link> : a.owner_name_text ?? '–') },
    { key: 'planting_year', label: 'Pflanzung', sortable: true, render: (a) => <span className="nowrap">{plantingLabel(a)}</span> },
    { key: 'hectares', label: 'ha', sortable: true, num: true, render: (a) => fmtHa(a.hectares) },
    { key: 'next_monitoring', label: 'Monitoring', sortable: true, render: (a) => <MonitoringPill m={a.monitoring} /> },
  ];
  return (
    <>
      <div className="head">
        <div>
          <h1>Flächen</h1>
          <div className="sub">{q.data ? `${q.data.total} Flächen` : ''}</div>
        </div>
        <div className="actions">
          {can('areas', 'write') && (
            <Link className="btn primary" to="/flaechen/neu">
              <Icon name="plus" /> Neue Fläche
            </Link>
          )}
        </div>
      </div>
      <div className="filterbar">
        <div className="field grow">
          <label htmlFor="a-q">Suche</label>
          <input id="a-q" value={state.q} onChange={(e) => update({ q: e.target.value })} placeholder="Name, Ort, Gemeinde, Gemarkung" />
        </div>
        <div className="field">
          <label htmlFor="a-status">Status</label>
          <Dropdown id="a-status" value={statusSel} onChange={(v) => setFilter('status', v ? [v] : [])} placeholder="Alle" options={AREA_STATUSES.filter((k) => statusCount(k) > 0 || k === statusSel).map((k) => ({ value: String(k), label: `${AREA_STATUS_LABELS[k]} (${statusCount(k)})` }))} />
        </div>
        <div className="field">
          <label htmlFor="a-state">Bundesland</label>
          <Dropdown id="a-state" value={stateSel} onChange={(v) => setFilter('state', v ? [v] : [])} placeholder="Alle" options={(facets.state ?? []).map((f) => ({ value: String(f.value), label: `${states.find((s) => s.code === f.value)?.label ?? f.value} (${f.count})` }))} />
        </div>
        <div className="spacer" />
        <div className="opts">
          <label>
            <input type="checkbox" checked={state.filters.archived?.[0] === '1'} onChange={(e) => setFilter('archived', e.target.checked ? ['1'] : [])} /> archivierte
          </label>
          {can('areas', 'write') && (
            <label>
              <input type="checkbox" checked={state.filters.review_needed?.[0] === '1'} onChange={(e) => setFilter('review_needed', e.target.checked ? ['1'] : [])} /> zu prüfen
            </label>
          )}
          {(stateSel || statusSel || state.q) && (
            <button type="button" className="btn sm" onClick={() => update({ q: '', filters: {} })}>
              Zurücksetzen
            </button>
          )}
        </div>
      </div>
      <div className="panel">
        {q.isLoading ? (
          <Skeleton rows={8} />
        ) : q.isError ? (
          <ErrorState error={q.error} retry={() => q.refetch()} />
        ) : (
          <DataTable
            columns={columns}
            rows={q.data!.items}
            sort={state.sort ? { key: state.sort, order: state.order ?? 'asc' } : undefined}
            onSort={toggleSort}
            onRowClick={(a) => nav(`/flaechen/${a.id}`)}
            rowClass={(a) => (a.archived_at ? 'archived' : '')}
            empty="Keine Flächen in dieser Auswahl"
            footer={<Pager page={q.data!.page} pageSize={q.data!.pageSize} total={q.data!.total} onPage={(p) => update({ page: p })} />}
          />
        )}
      </div>
    </>
  );
}
